import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutData } from '@/mocks/about';

const AboutPage = () => {
  const { brandName, brandIntro, mission, vision, values, stats, timeline, reputations, contact, faq } = aboutData;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${brandName}`,
    description: brandIntro,
    url: 'https://www.yaojingmanhua.com/about',
    mainEntity: {
      '@type': 'Organization',
      name: brandName,
      description: brandIntro,
      email: contact.email,
      telephone: contact.phone,
      address: {
        '@type': 'PostalAddress',
        streetAddress: contact.address,
      },
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  return (
    <>
      <SEOHead
        title={`关于我们 - ${brandName}`}
        description={brandIntro}
        keywords="妖精漫画,关于我们,韩漫平台,免费漫画,品牌介绍"
        canonicalUrl="/about"
        schema={{ ...schema, ...faqSchema }}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        {/* Brand Introduction */}
        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
              关于{ brandName }
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-3xl mx-auto">
              {brandIntro}
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center p-5 bg-background-50 rounded-lg">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                    <i className={`${stat.icon} text-xl text-accent-500`}></i>
                  </div>
                  <p className="text-2xl md:text-3xl font-bold text-foreground-900">{stat.value}</p>
                  <p className="text-sm text-foreground-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-background-100 rounded-lg p-6">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 mb-4">
                  <i className="ri-flag-line text-lg text-primary-500"></i>
                </div>
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-3">我们的使命</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{mission}</p>
              </div>
              <div className="bg-background-100 rounded-lg p-6">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 mb-4">
                  <i className="ri-eye-line text-lg text-accent-500"></i>
                </div>
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-3">我们的愿景</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{vision}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
              核心价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {values.map((value) => (
                <div key={value.title} className="bg-background-50 rounded-lg p-5 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className={`${value.icon} text-xl text-primary-500`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{value.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-5 top-0 bottom-0 w-px bg-background-200"></div>
              <div className="space-y-8">
                {timeline.map((item, index) => (
                  <div key={item.year} className="relative pl-14">
                    <div className={`absolute left-0 w-10 h-10 rounded-full flex items-center justify-center ${
                      index === timeline.length - 1 ? 'bg-primary-500' : 'bg-background-100'
                    }`}>
                      {index === timeline.length - 1 ? (
                        <i className="ri-star-line text-background-50"></i>
                      ) : (
                        <i className="ri-circle-fill text-xs text-foreground-300"></i>
                      )}
                    </div>
                    <div>
                      <span className="text-xs font-bold text-primary-500 bg-primary-50 px-2 py-0.5 rounded whitespace-nowrap">{item.year}</span>
                      <h3 className="text-base font-semibold text-foreground-900 mt-1.5">{item.title}</h3>
                      <p className="text-sm text-foreground-500 mt-1 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-2">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">听听用户们怎么说</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {reputations.map((rep) => (
                <div key={rep.name} className="bg-background-50 rounded-lg p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={rep.avatar}
                      alt={rep.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{rep.name}</p>
                      <p className="text-xs text-foreground-400">{rep.date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 mb-2">
                    {Array.from({ length: rep.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{rep.comment}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
              常见问题
            </h2>
            <div className="space-y-3">
              {faq.map((item, index) => (
                <details key={index} className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors list-none">
                    <span className="text-sm font-medium text-foreground-800 pr-4">{item.question}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                    <i className="ri-mail-line text-primary-500"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">电子邮箱</p>
                    <a href={`mailto:${contact.email}`} className="text-sm text-primary-500 hover:text-primary-600 transition-colors">
                      {contact.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                    <i className="ri-phone-line text-accent-500"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">联系电话</p>
                    <p className="text-sm text-foreground-600">{contact.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 flex-shrink-0">
                    <i className="ri-map-pin-line text-secondary-500"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">公司地址</p>
                    <p className="text-sm text-foreground-600">{contact.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                    <i className="ri-weibo-line text-primary-500"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">社交媒体</p>
                    <p className="text-sm text-foreground-600">微博：{contact.weibo}</p>
                    <p className="text-sm text-foreground-600">QQ群：{contact.qqGroup}</p>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 rounded-lg p-6">
                <h3 className="text-base font-semibold text-foreground-900 mb-4">发送消息</h3>
                <form
                  id="contact-form"
                  data-readdy-form
                  action="https://readdy.ai/api/form/d9pcu40senbniolest2g"
                  method="POST"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const form = e.currentTarget;
                    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement);
                    if (honeypot && honeypot.value.trim()) {
                      alert('消息已发送！我们会尽快回复您。');
                      form.reset();
                      return;
                    }
                    const formData = new FormData(form);
                    fetch(form.action, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                      body: new URLSearchParams(Object.fromEntries(formData) as Record<string, string>).toString(),
                    })
                      .then((res) => res.text())
                      .then((text) => {
                        let parsed;
                        try { parsed = JSON.parse(text); } catch { parsed = null; }
                        if (parsed?.code === 'OK') {
                          alert('消息已发送！我们会尽快回复您。');
                          form.reset();
                        } else {
                          const msg = parsed?.meta?.message || parsed?.message || '发送失败，请稍后再试';
                          alert(msg);
                        }
                      })
                      .catch(() => alert('网络错误，请稍后再试'));
                  }}
                >
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1">姓名</label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-3 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1">邮箱</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-3 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1">消息</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-3 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                        placeholder="请输入您的消息（500字以内）"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="anti-spam-field"
                    />
                    <button
                      type="submit"
                      className="w-full py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      发送消息
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default AboutPage;